import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useProject, useCreateProject, useUpdateProject } from '../../hooks/useProjects';
import { permissionTagsAPI } from '../../services/api';
import { usePermission } from '../../contexts/PermissionContext';
import { STEPS, INITIAL_FORM_DATA } from './constants';
import StepIndicator from './StepIndicator';
import BasicInfoStep from './steps/BasicInfoStep';
import TimeScopeStep from './steps/TimeScopeStep';
import ReviewStep from './steps/ReviewStep';

function ProjectForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const { userProfile, hasWildcardAccess, getVisibleTags } = usePermission();

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [permissionTags, setPermissionTags] = useState([]);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});

  const { data: projectData, isLoading: loadingProject } = useProject(
    id,
    {},
    { enabled: isEditMode }
  );

  const createProject = useCreateProject({
    onSuccess: (response) => {
      navigate(`/projects/${response.data.id}`);
    },
    onError: (error) => {
      toast.error(`建立失敗: ${error.message}`);
    },
  });

  const updateProject = useUpdateProject({
    onSuccess: () => {
      navigate(`/projects/${id}`);
    },
    onError: (error) => {
      toast.error(`更新失敗: ${error.message}`);
    },
  });

  // Load permission tags
  useEffect(() => {
    const loadPermissionTags = async () => {
      try {
        const response = await permissionTagsAPI.getAll();
        setPermissionTags(response.data || []);
      } catch (error) {
        console.warn('載入權限標籤失敗，使用空陣列:', error);
        setPermissionTags([]);
      }
    };
    loadPermissionTags();
  }, []);

  useEffect(() => {
    if (isEditMode && projectData?.data) {
      const project = projectData.data;
      setFormData({
        name: project.name || '',
        code: project.code || '',
        description: project.description || '',
        project_type: project.project_type || 'quarterly',
        category: project.category || 'vendor_assessment',
        status: project.status || 'draft',
        access_tags: project.access_tags || [],
        time_scope: {
          year: project.time_scope?.year || new Date().getFullYear(),
          quarter: project.time_scope?.quarter || null,
          half: project.time_scope?.half || null,
          start_date: project.time_scope?.start_date || '',
          end_date: project.time_scope?.end_date || '',
        },
        form_template_ids: project.form_template_ids || [],
        eligible_vendor_ids: project.eligible_vendor_ids || [],
        eligible_vendor_tags: project.eligible_vendor_tags || [],
        settings: project.settings || {
          auto_reminder: true,
          reminder_days: 3,
          allow_late_submission: false,
          require_approval: true,
        },
        team: project.team || { owner: '', managers: [] },
        tags: project.tags || [],
        budget: project.budget || { total: 0, currency: 'TWD' },
      });
    }
  }, [isEditMode, projectData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleTimeScopeChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      time_scope: { ...prev.time_scope, [field]: value },
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleBudgetChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      budget: { ...prev.budget, [field]: value },
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.name.trim()) {
        newErrors.name = '請輸入專案名稱';
      }
      if (permissionTags.length > 0 && formData.access_tags.length === 0) {
        newErrors.access_tags = '請選擇權限標籤';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleStepClick = (step) => {
    if (completedSteps.includes(step - 1) || step <= currentStep) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    const cleanedTimeScope = { year: formData.time_scope.year };

    if (formData.project_type === 'quarterly') {
      cleanedTimeScope.quarter = formData.time_scope.quarter;
    } else if (formData.project_type === 'semi-annual') {
      cleanedTimeScope.half = formData.time_scope.half;
    } else if (formData.project_type === 'ad-hoc' || formData.project_type === 'campaign') {
      cleanedTimeScope.start_date = formData.time_scope.start_date;
      cleanedTimeScope.end_date = formData.time_scope.end_date;
    }

    const submitData = { ...formData, time_scope: cleanedTimeScope };

    if (isEditMode) {
      updateProject.mutate({ id, data: submitData });
    } else {
      createProject.mutate(submitData);
    }
  };

  if (isEditMode && loadingProject) {
    return (
      <div className="min-h-screen bg-purple-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear - 2 + i);

  return (
    <div className="min-h-screen bg-purple-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 返回按鈕 */}
        <Link
          to={isEditMode ? `/projects/${id}` : '/projects'}
          className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-6"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          返回
        </Link>

        {/* Wizard Container */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-8 py-6 text-white">
            <h1 className="text-3xl font-bold">
              {isEditMode ? '編輯專案' : '建立新專案'}
            </h1>
            <p className="mt-2 text-purple-100">
              {STEPS.find(s => s.id === currentStep)?.description}
            </p>
          </div>

          {/* Step Progress Indicator */}
          <StepIndicator
            currentStep={currentStep}
            completedSteps={completedSteps}
            onStepClick={handleStepClick}
          />

          {/* Step Content */}
          <div className="px-8 py-8">
            <div className="max-w-3xl mx-auto">
              {currentStep === 1 && (
                <BasicInfoStep
                  formData={formData}
                  errors={errors}
                  permissionTags={permissionTags}
                  hasWildcardAccess={hasWildcardAccess}
                  userProfile={userProfile}
                  getVisibleTags={getVisibleTags}
                  onChange={handleChange}
                />
              )}

              {currentStep === 2 && (
                <TimeScopeStep
                  formData={formData}
                  errors={errors}
                  yearOptions={yearOptions}
                  onTimeScopeChange={handleTimeScopeChange}
                  onBudgetChange={handleBudgetChange}
                />
              )}

              {currentStep === 3 && (
                <ReviewStep
                  formData={formData}
                  onEditStep={setCurrentStep}
                />
              )}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="bg-gray-50 px-8 py-6 border-t border-gray-200">
            <div className="max-w-3xl mx-auto flex gap-3">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
                >
                  ← 上一步
                </button>
              )}

              <div className="flex-1" />

              {currentStep < STEPS.length ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium shadow-md"
                >
                  下一步 →
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={createProject.isPending || updateProject.isPending}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {createProject.isPending || updateProject.isPending ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      處理中...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {isEditMode ? '更新專案' : '建立專案'}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectForm;
